import { z } from 'zod';
import { IdSchema, PublicationIdSchema, TenantIdSchema } from './common.js';

/*
 * Contrato 1: formato do catálogo.
 * Um arquivo YAML por assunto (contexto/<assunto>/assunto.yaml). As chaves ficam em português
 * porque o arquivo é escrito e revisado pelos donos do assunto, não por desenvolvedores.
 */

/** Campos de histórico que todo item do catálogo pode carregar. */
const HistoryFields = {
  versao: z.number().int().min(1).default(1),
  alterado_por: z.string().min(1).optional(),
  alterado_em: z.string().min(1).optional(),
  motivo: z.string().min(1).optional(),
};

/** Nome de campo do Qlik (ou coluna da fonte). Vem do catálogo, nunca da LLM. */
const FieldNameSchema = z
  .string()
  .min(1)
  .max(256)
  .refine((s) => !/[\r\n\0]/.test(s), 'nome de campo não pode ter quebra de linha');

/**
 * texto: valores como estão na fonte. periodo_mensal e ano: campo que já guarda o período.
 * data: campo de data ou timestamp; o período (dia, mês, trimestre, ano) é derivado na consulta.
 */
export const DimensionTypeSchema = z.enum(['texto', 'periodo_mensal', 'ano', 'data']);
export type DimensionType = z.infer<typeof DimensionTypeSchema>;

/** Grão de um período derivado de uma dimensão do tipo data. */
export const GrainSchema = z.enum(['dia', 'mes', 'trimestre', 'ano']);
export type Grain = z.infer<typeof GrainSchema>;

export const DimensionSchema = z
  .object({
    id: IdSchema,
    nome: z.string().min(1),
    descricao: z.string().optional(),
    sinonimos: z.array(z.string().min(1)).default([]),
    campo: FieldNameSchema,
    tipo: DimensionTypeSchema.default('texto'),
    /** Dado pessoal (CPF, nome de contribuinte): fica fora de list_values e da LLM, e é mascarado no resultado. */
    sensivel: z.boolean().default(false),
    /**
     * Como o valor aparece na fonte, para períodos. Tokens: YYYY, YY, MM, M, MMM (usa nomes_meses).
     * O parâmetro sempre chega no formato canônico (2025-09) e o núcleo converte.
     */
    formato: z.string().min(1).optional(),
    nomes_meses: z.array(z.string().min(1)).length(12).optional(),
    ...HistoryFields,
  })
  .superRefine((d, ctx) => {
    if (d.formato && (d.tipo === 'texto' || d.tipo === 'data')) {
      ctx.addIssue({ code: 'custom', path: ['formato'], message: 'formato só vale para tipo periodo_mensal ou ano' });
    }
    if (d.formato?.includes('MMM') && !d.nomes_meses) {
      ctx.addIssue({ code: 'custom', path: ['nomes_meses'], message: 'formato com MMM exige nomes_meses (12 nomes)' });
    }
  });
export type Dimension = z.infer<typeof DimensionSchema>;

export const MetricSchema = z.object({
  id: IdSchema,
  nome: z.string().min(1),
  descricao: z.string().min(1),
  sinonimos: z.array(z.string().min(1)).default([]),
  /** Código de moeda ISO 4217 (BRL, USD), "percentual" ou qualquer texto de unidade ("contribuintes"). */
  unidade: z.string().min(1).default('numero'),
  casas_decimais: z.number().int().min(0).max(6).optional(),
  /** Expressão na linguagem da fonte. Precisa ter ao menos uma agregação (Sum, Count...). */
  expressao: z
    .string()
    .min(1)
    .max(4000)
    .refine((s) => !s.includes('$('), 'a expressão não pode usar expansão $( ); declare o valor no catálogo'),
  ...HistoryFields,
});
export type Metric = z.infer<typeof MetricSchema>;

export const QueryParameterSchema = z.object({
  dimensao: IdSchema,
  obrigatorio: z.boolean().default(true),
  descricao: z.string().optional(),
});
export type QueryParameter = z.infer<typeof QueryParameterSchema>;

const ApprovalSchema = z.object({ por: z.string().min(1), em: z.string().min(1) });

export const ValidatedQuerySchema = z.object({
  id: IdSchema,
  exemplo: z.string().min(1),
  /** Outras formas de fazer a mesma pergunta; ajudam a busca, não mudam o cálculo. */
  exemplos: z.array(z.string().min(1)).default([]),
  descricao: z.string().optional(),
  metricas: z.array(IdSchema).min(1),
  /** Dimensões de agrupamento da resposta (linhas da tabela). Vazio: um número só. */
  dimensoes: z.array(IdSchema).default([]),
  /** Agrupa por um período derivado de uma dimensão do tipo data. Vem antes das outras dimensões. */
  periodo: z.object({ dimensao: IdSchema, grao: GrainSchema }).optional(),
  parametros: z.record(IdSchema, QueryParameterSchema).default({}),
  /** Máximo de linhas desta query; o teto global da instância continua valendo. */
  limite: z.number().int().min(1).max(100_000).optional(),
  certificada_por: z.string().min(1).optional(),
  aprovacoes: z.object({ negocio: ApprovalSchema.optional(), tecnico: ApprovalSchema.optional() }).optional(),
  ...HistoryFields,
});
export type ValidatedQuery = z.infer<typeof ValidatedQuerySchema>;

export const PlaybookSchema = z.object({
  id: IdSchema,
  quando: z.string().min(1),
  passos: z.array(z.string().min(1)).min(1),
  ...HistoryFields,
});
export type Playbook = z.infer<typeof PlaybookSchema>;

function findDuplicates(ids: string[]): string[] {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const id of ids) (seen.has(id) ? dup : seen).add(id);
  return [...dup];
}

/**
 * Seções de texto do contexto de um app, que o agente lê junto com a camada semântica.
 * correcoes vem só de pessoas e prevalece sobre as regras; restricoes é obrigatório seguir; tom é o tom de voz das respostas
 * sobre o app, por cima do tom geral.
 */
export const SectionKeySchema = z.enum(['contexto', 'regras', 'premissas', 'restricoes', 'vocabulario', 'sinonimos', 'exemplos', 'correcoes', 'tom']);
export type SectionKey = z.infer<typeof SectionKeySchema>;
export const SECTION_KEYS = SectionKeySchema.options;
export const SectionsSchema = z.partialRecord(SectionKeySchema, z.string().max(50_000));
export type Sections = z.infer<typeof SectionsSchema>;

const SubjectObject = z.object({
    id: IdSchema,
    nome: z.string().min(1),
    descricao: z.string().optional(),
    donos: z.object({ negocio: z.string().min(1), tecnico: z.string().min(1) }),
    // Quem enxerga o assunto é a fonte (ADR 0036). Versões antigas ainda trazem `papel`: o zod descarta chave desconhecida.
    /** Id da conexão em sources[] do lastrouter.yaml. */
    fonte: IdSchema,
    /** Id do app na fonte (no Qlik, o GUID do app). */
    app: z.string().min(1).max(256),
    dimensoes: z.array(DimensionSchema).default([]),
    // Pode ficar vazia (29/10/2026): sem medida mestre, o query monta a agregação pelos campos do app (ADR 0037).
    metricas: z.array(MetricSchema).default([]),
    queries_validadas: z.array(ValidatedQuerySchema).default([]),
    roteiros: z.array(PlaybookSchema).default([]),
    secoes: SectionsSchema.default({}),
  });

/** Assunto em edição. A validação completa (SubjectSchema, com as referências cruzadas) vale ao publicar. */
export const SubjectDraftSchema = SubjectObject;
export type SubjectDraft = z.infer<typeof SubjectDraftSchema>;

export const SubjectSchema = SubjectObject.superRefine((s, ctx) => {
    const lists = [
      ['dimensoes', s.dimensoes.map((d) => d.id)],
      ['metricas', s.metricas.map((m) => m.id)],
      ['queries_validadas', s.queries_validadas.map((q) => q.id)],
      ['roteiros', s.roteiros.map((r) => r.id)],
    ] as const;
    for (const [key, ids] of lists) {
      for (const dup of findDuplicates([...ids])) {
        ctx.addIssue({ code: 'custom', path: [key], message: `id repetido: ${dup}` });
      }
    }
    const dims = new Set(s.dimensoes.map((d) => d.id));
    const metrics = new Set(s.metricas.map((m) => m.id));
    s.queries_validadas.forEach((q, qi) => {
      q.metricas.forEach((m, mi) => {
        if (!metrics.has(m)) {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'metricas', mi], message: `métrica "${m}" não existe neste assunto` });
        }
      });
      q.dimensoes.forEach((d, di) => {
        if (!dims.has(d)) {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'dimensoes', di], message: `dimensão "${d}" não existe neste assunto` });
        }
      });
      if (q.periodo) {
        const pd = s.dimensoes.find((d) => d.id === q.periodo!.dimensao);
        if (!pd) {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'periodo', 'dimensao'], message: `dimensão "${q.periodo.dimensao}" não existe neste assunto` });
        } else if (pd.tipo !== 'data') {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'periodo', 'dimensao'], message: `período só vale para dimensão do tipo data; "${pd.id}" é ${pd.tipo}` });
        }
        if (q.dimensoes.includes(q.periodo.dimensao)) {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'dimensoes'], message: `"${q.periodo.dimensao}" já está no período; tire de dimensoes` });
        }
      }
      q.dimensoes.forEach((d, di) => {
        if (s.dimensoes.find((x) => x.id === d)?.tipo === 'data') {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'dimensoes', di], message: `dimensão de data "${d}" agrupa por período; use periodo com o grão` });
        }
      });
      const paramDims = Object.values(q.parametros).map((p) => p.dimensao);
      for (const dup of findDuplicates(paramDims)) {
        // Dois parâmetros na mesma dimensão virariam um filtro "ou" e somariam os dois valores.
        ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'parametros'], message: `dois parâmetros na dimensão "${dup}"; use um só` });
      }
      for (const [name, p] of Object.entries(q.parametros)) {
        if (!dims.has(p.dimensao)) {
          ctx.addIssue({ code: 'custom', path: ['queries_validadas', qi, 'parametros', name, 'dimensao'], message: `dimensão "${p.dimensao}" não existe neste assunto` });
        }
      }
    });
  });
export type Subject = z.infer<typeof SubjectSchema>;
export type SubjectInput = z.input<typeof SubjectSchema>;

/** Prompt versionado junto com o catálogo (contexto/_prompts/<id>.yaml). */
export const PromptSchema = z.object({
  id: IdSchema,
  versao: z.number().int().min(1),
  texto: z.string().min(1),
});
export type Prompt = z.infer<typeof PromptSchema>;

/** Foto completa do catálogo: o que uma publicação congela. */
export const CatalogSnapshotSchema = z
  .object({
    formato: z.literal(1).default(1),
    subjects: z.array(SubjectSchema),
    prompts: z.array(PromptSchema).default([]),
    /** Instruções do agente por canal (contexto/_agente/<canal>.md), versionadas com o catálogo. */
    // A chave vira nome de arquivo (_agente/<canal>.md): só id, nada de caminho.
    agent_instructions: z.record(IdSchema, z.string().max(100_000)).default({}),
  })
  .superRefine((c, ctx) => {
    for (const dup of findDuplicates(c.subjects.map((s) => s.id))) {
      ctx.addIssue({ code: 'custom', path: ['subjects'], message: `assunto repetido: ${dup}` });
    }
    const queryIds = c.subjects.flatMap((s) => s.queries_validadas.map((q) => q.id));
    for (const dup of findDuplicates(queryIds)) {
      ctx.addIssue({ code: 'custom', path: ['subjects'], message: `query validada "${dup}" aparece em mais de um assunto; o id precisa ser único no catálogo` });
    }
    for (const dup of findDuplicates(c.prompts.map((p) => p.id))) {
      ctx.addIssue({ code: 'custom', path: ['prompts'], message: `prompt repetido: ${dup}` });
    }
  });
export type CatalogSnapshot = z.infer<typeof CatalogSnapshotSchema>;

/** Metadados de uma publicação. O conteúdo fica endereçado pelo hash. */
export const PublicationSchema = z.object({
  id: PublicationIdSchema,
  tenant_id: TenantIdSchema,
  content_hash: z.string().regex(/^[0-9a-f]{64}$/),
  /** Commit do repositório de contexto, quando a pasta é um repositório Git. */
  commit: z.string().nullable(),
  /** Havia mudanças não commitadas na pasta no momento da publicação. */
  dirty: z.boolean(),
  created_at: z.string(),
  created_by: z.string().min(1),
  reason: z.string().optional(),
  parent_id: PublicationIdSchema.nullable(),
  /** Quando a publicação volta para o conteúdo de uma anterior. */
  restored_from: PublicationIdSchema.optional(),
  summary: z.array(z.string()),
});
export type Publication = z.infer<typeof PublicationSchema>;
